import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import { useState } from "react";
import { IAddOrInviteResellerClientParams } from "../../../clients/answeringClient";
import { cpfValidation } from "../../../PsichologistManagement/components/CpfInputField";
import {
  CrpInputField,
  validateCpr,
} from "../../../PsichologistManagement/components/CrpInputField";

interface Props {
  isOpen: boolean;
  isLoading: boolean;
  error?: string;
  onClose: () => void;
  onConfirm: (data: IAddOrInviteResellerClientParams) => void;
}

export function AddClientModal({
  isOpen,
  isLoading,
  error,
  onClose,
  onConfirm,
}: Props) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isPsychologist, setIsPsychologist] = useState(false);
  const [cpf, setCpf] = useState("");
  const [crp, setCrp] = useState("");

  const formatCpf = (digits: string): string => {
    let v = digits;
    if (v.length > 3) v = v.slice(0, 3) + "." + v.slice(3);
    if (v.length > 7) v = v.slice(0, 7) + "." + v.slice(7);
    if (v.length > 11) v = v.slice(0, 11) + "-" + v.slice(11);
    return v.slice(0, 14);
  };

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, "");
    setCpf(formatCpf(digits));
  };

  const handleClose = () => {
    setEmail("");
    setFirstName("");
    setLastName("");
    setIsPsychologist(false);
    setCpf("");
    setCrp("");
    onClose();
  };

  const handleConfirm = () => {
    if (!email || !firstName || !lastName) return;

    const isCpfValid = cpf.length === 0 || cpfValidation(cpf);
    const { isValid: isCrpValid } = validateCpr(crp);
    if (!isCpfValid || !isCrpValid) return;

    onConfirm({
      email,
      firstName,
      lastName,
      isPsychologist,
      cpf: cpf || undefined,
      crp: crp || undefined,
    });

    setEmail("");
    setFirstName("");
    setLastName("");
    setIsPsychologist(false);
    setCpf("");
    setCrp("");
  };

  const isCpfValid = cpf.length === 0 || cpfValidation(cpf);
  const { isValid: isCrpValid } = validateCpr(crp);
  const showCpfError = cpf.length > 0 && !isCpfValid;
  const showCrpError = crp.length > 0 && !isCrpValid;

  const isFormInvalid =
    !email ||
    !firstName ||
    !lastName ||
    (isPsychologist && (!isCpfValid || !isCrpValid));

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth variant="modal">
      <DialogTitle variant="modal">
        <PersonAddAltIcon />
        Adicionar Novo Cliente
      </DialogTitle>
      <DialogContent variant="modal">
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Digite os dados do cliente. Se ele não tiver uma conta, um convite será enviado.
        </Typography>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              fullWidth
              required
              label="Nome do Cliente"
              variant="outlined"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
            <TextField
              fullWidth
              required
              label="Sobrenome do Cliente"
              variant="outlined"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
            <TextField
              fullWidth
              required
              label="E-mail do Cliente"
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value.trim())}
              error={!!error}
              helperText={error}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={isPsychologist}
                  onChange={(e) => {
                    setIsPsychologist(e.target.checked);
                    if (!e.target.checked) {
                      setCpf("");
                      setCrp("");
                    }
                  }}
                />
              }
              label="Cliente é Psicólogo(a)"
            />

            {isPsychologist && (
              <>
                <TextField
                  fullWidth
                  label="CPF do Cliente *"
                  variant="outlined"
                  value={cpf}
                  onChange={handleCpfChange}
                  error={showCpfError}
                  helperText={
                    showCpfError ? "Formato de CPF inválido (XXX.XXX.XXX-XX)" : ""
                  }
                  inputProps={{ maxLength: 14 }}
                />

                <CrpInputField
                  label="CRP do Cliente *"
                  value={crp}
                  onChange={setCrp}
                  error={showCrpError}
                  helperText={
                    showCrpError
                      ? "Formato de CRP inválido (XX/YYYYY com região entre 01 e 24)"
                      : ""
                  }
                />

                {(showCpfError || showCrpError) && (
                  <Alert severity="error">
                    {showCpfError && (
                      <>
                        Por favor, informe um CPF válido. Ex: 123.456.789-00
                        <br />
                      </>
                    )}
                    {showCrpError && (
                      <>
                        Por favor, use XX/YYYYY com região entre 01 e 24. Ex: 06/66501
                      </>
                    )}
                  </Alert>
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions
        variant="modal"
        sx={{ justifyContent: "end", flexWrap: "wrap", gap: 1, mt: 1 }}
      >
        <Button variant="outlined" color="error" onClick={handleClose}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isFormInvalid || isLoading}
        >
          Adicionar Cliente
        </Button>
      </DialogActions>
    </Dialog>
  );
}
